export type DepartmentLevel = "department_id" | "category_id" | "subcategory_id";

export type ProductFilterValues = Record<DepartmentLevel, number[]> & {
  type: string[];
  vendor_id: number[];
  sales_channel_type: "online_store" | null;
  /** Include products that are not attached to this facility. */
  all_facilities: boolean;
};

export interface IDepartmentNode {
  id: number;
  title: string;
  level: DepartmentLevel;
  children: IDepartmentNode[];
}

export function emptyProductFilters(): ProductFilterValues {
  return {
    type: [],
    vendor_id: [],
    department_id: [],
    category_id: [],
    subcategory_id: [],
    sales_channel_type: null,
    all_facilities: false,
  };
}

export function countProductFilters(filters: ProductFilterValues): number {
  return (
    filters.type.length +
    filters.vendor_id.length +
    filters.department_id.length +
    filters.category_id.length +
    filters.subcategory_id.length +
    (filters.sales_channel_type ? 1 : 0) +
    (filters.all_facilities ? 1 : 0)
  );
}

export function formatNamesWithCount(names: string[], maxVisible: number): string {
  if (names.length <= maxVisible) {
    return names.join(", ");
  }
  return `${names.slice(0, maxVisible).join(", ")}, +${names.length - maxVisible}`;
}

type DepartmentRecord = { id: number; title: string; parent_id?: number | null };

export function buildDepartmentTree(
  departments: DepartmentRecord[] | null,
  categories: DepartmentRecord[] | null,
  subcategories: DepartmentRecord[] | null,
): IDepartmentNode[] {
  return (departments ?? []).map(department => ({
    id: department.id,
    title: department.title,
    level: "department_id",
    children: (categories ?? [])
      .filter(category => category.parent_id === department.id)
      .map(category => ({
        id: category.id,
        title: category.title,
        level: "category_id",
        children: (subcategories ?? [])
          .filter(subcategory => subcategory.parent_id === category.id)
          .map(subcategory => ({
            id: subcategory.id,
            title: subcategory.title,
            level: "subcategory_id" as const,
            children: [] as IDepartmentNode[],
          })),
      })),
  }));
}

export function nodeKey(node: Pick<IDepartmentNode, "id" | "level">) {
  return `${node.level}:${node.id}`;
}

export function flattenDepartmentTree(tree: IDepartmentNode[]): IDepartmentNode[] {
  return tree.reduce<IDepartmentNode[]>(
    (nodes, node) => [...nodes, node, ...flattenDepartmentTree(node.children)],
    [],
  );
}

export function selectedDepartmentKeys(filters: ProductFilterValues): Set<string> {
  return new Set([
    ...filters.department_id.map(id => nodeKey({ id, level: "department_id" })),
    ...filters.category_id.map(id => nodeKey({ id, level: "category_id" })),
    ...filters.subcategory_id.map(id => nodeKey({ id, level: "subcategory_id" })),
  ]);
}

function findPath(tree: IDepartmentNode[], target: IDepartmentNode): IDepartmentNode[] | null {
  for (const node of tree) {
    if (nodeKey(node) === nodeKey(target)) {
      return [node];
    }
    const childPath = findPath(node.children, target);
    if (childPath) {
      return [node, ...childPath];
    }
  }
  return null;
}

/**
 * A selected node stands for its whole subtree, so the selection always holds the top-most fully selected nodes.
 * Unchecking a node covered by a selected ancestor re-selects that ancestor's other branches instead.
 */
export function toggleDepartmentNode(
  tree: IDepartmentNode[],
  filters: ProductFilterValues,
  target: IDepartmentNode,
): ProductFilterValues {
  const selected = selectedDepartmentKeys(filters);
  const path = findPath(tree, target) ?? [target];
  const coveringIndex = path.findIndex(node => selected.has(nodeKey(node)));

  if (coveringIndex > -1) {
    selected.delete(nodeKey(path[coveringIndex]));
    for (let index = coveringIndex; index < path.length - 1; index++) {
      path[index].children
        .filter(child => nodeKey(child) !== nodeKey(path[index + 1]))
        .forEach(child => selected.add(nodeKey(child)));
    }
  } else {
    const fullTarget = path[path.length - 1];
    flattenDepartmentTree(fullTarget.children).forEach(node => selected.delete(nodeKey(node)));
    selected.add(nodeKey(fullTarget));

    for (let index = path.length - 2; index >= 0; index--) {
      const parent = path[index];
      if (!parent.children.every(child => selected.has(nodeKey(child)))) {
        break;
      }
      parent.children.forEach(child => selected.delete(nodeKey(child)));
      selected.add(nodeKey(parent));
    }
  }

  const idsFor = (level: DepartmentLevel) =>
    [...selected].filter(key => key.startsWith(`${level}:`)).map(key => Number(key.split(":")[1]));

  return {
    ...filters,
    department_id: idsFor("department_id"),
    category_id: idsFor("category_id"),
    subcategory_id: idsFor("subcategory_id"),
  };
}
